import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { History, Play, Clock, ShieldOff, Plus, Sparkles } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  runComplianceReplay,
  runHistoricalComplianceReplay,
  listComplianceReplayRegulators,
  type Regulator,
  type RunReplayResult,
} from '@/lib/api';

// Wave 5 (2026-05-27) - REGULATORS is now a fallback; live data
// comes from /v1/frontier/replay/regulators which intersects with
// the tenant's activated industry packs.
const FALLBACK_REGULATORS = [
  { id: 'dpdpa', name: 'DPDPA (India)', clock: 72, section9: true, activeForTenant: true },
  { id: 'gdpr', name: 'GDPR (EU)', clock: 72, section9: true, activeForTenant: true },
  { id: 'ccpa', name: 'CCPA (US)', clock: 24, section9: true, activeForTenant: true },
  { id: 'hipaa', name: 'HIPAA (US)', clock: 60 * 24, section9: true, activeForTenant: true },
  { id: 'pci_dss', name: 'PCI-DSS', clock: 24, section9: false, activeForTenant: true },
  { id: 'rbi_dpf', name: 'RBI Cyber Sec Framework', clock: 6, section9: false, activeForTenant: true },
  { id: 'sebi_csscb', name: 'SEBI CSCRF', clock: 24, section9: false, activeForTenant: true },
  { id: 'uidai_2025_circular', name: 'UIDAI 2025 Circular', clock: 6, section9: true, activeForTenant: true },
  { id: 'sec_5b', name: 'SEC Form 8-K Item 1.05', clock: 96, section9: false, activeForTenant: true },
  { id: 'pdpa_sg', name: 'Singapore PDPA', clock: 72, section9: true, activeForTenant: true },
  { id: 'lgpd_brazil', name: 'Brazil LGPD', clock: 48, section9: true, activeForTenant: true },
  { id: 'pipl', name: 'China PIPL', clock: 24, section9: true, activeForTenant: true },
  { id: 'pipeda', name: 'Canada PIPEDA', clock: 24, section9: true, activeForTenant: true },
];

const RECENT_REPLAYS = [
  {
    id: 'r1',
    name: 'GDPR + RBI cutover dry-run',
    hypothesis: 'If RBI 6h breach clock had applied 90 days earlier and Section 9 had been enforced for minor data, what changes?',
    regulators: ['rbi_dpf', 'dpdpa'],
    events: 8_402,
    deltas: 247,
    newHardBlocks: 12,
    clockTightenings: 38,
    when: '2h ago',
  },
  {
    id: 'r2',
    name: 'PIPL sovereignty dry-run',
    hypothesis: 'If we added China PIPL adequacy + 24h clock, how many cross-border events change?',
    regulators: ['pipl'],
    events: 12_407,
    deltas: 89,
    newHardBlocks: 4,
    clockTightenings: 22,
    when: '1d ago',
  },
];

// Mapping: dpdpa-style ids in the UI match what the API expects (mostly).
// uidai_2025 needs to map to uidai_2025_circular.
const ID_TO_REGULATOR: Record<string, Regulator> = {
  dpdpa: 'dpdpa', gdpr: 'gdpr', ccpa: 'ccpa', hipaa: 'hipaa',
  pci_dss: 'pci_dss', rbi_dpf: 'rbi_dpf', sebi_csscb: 'sebi_csscb',
  uidai_2025: 'uidai_2025_circular', sec_5b: 'sec_5b',
  pdpa_sg: 'pdpa_sg', lgpd_brazil: 'lgpd_brazil', pipl: 'pipl', pipeda: 'pipeda',
};

export function FrontierReplay() {
  const [selected, setSelected] = useState<string[]>(['rbi_dpf', 'dpdpa']);
  const [hypothesis, setHypothesis] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RunReplayResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [liveRegulators, setLiveRegulators] = useState<typeof FALLBACK_REGULATORS | null>(null);
  // T1.5 - mode: 'historical' pulls the tenant's real activity_events +
  // ddr_decisions + breach_incidents over the window; 'synthetic' replays
  // an 8-event illustrative sample.
  const [mode, setMode] = useState<'historical' | 'synthetic'>('historical');
  const [windowDays, setWindowDays] = useState<7 | 30 | 90>(30);

  useEffect(() => {
    listComplianceReplayRegulators()
      .then((res) => {
        if (!res.regulators.length) return;
        // Adapt server shape to the local card shape.
        setLiveRegulators(
          res.regulators.map((r) => ({
            id: r.key,
            name: r.key
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (c) => c.toUpperCase()),
            clock: r.breachClockHours,
            section9: r.enforcesSection9Equivalent,
            activeForTenant: r.activeForTenant,
          })),
        );
      })
      .catch(() => {
        /* keep fallback */
      });
  }, []);
  const REGULATORS = liveRegulators ?? FALLBACK_REGULATORS;

  function toggle(id: string) {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  async function handleRun(): Promise<void> {
    if (selected.length === 0) return;
    setError(null);
    setRunning(true);
    setResult(null);
    try {
      const now = new Date();
      const regs = selected.map((id) => ID_TO_REGULATOR[id]).filter(Boolean) as Regulator[];
      const replayName = hypothesis || `Counter-factual ${new Date().toISOString().slice(0, 16)}`;

      // T1.5 - historical mode: pull the tenant's real events over the
      // selected window. No synthetic events; the backend queries
      // ClickHouse + Postgres directly.
      if (mode === 'historical') {
        const from = new Date(now.getTime() - windowDays * 24 * 60 * 60 * 1000);
        const res = await runHistoricalComplianceReplay({
          replayName,
          fromTimestamp: from.toISOString(),
          toTimestamp: now.toISOString(),
          targetRegulators: regs,
          ...(hypothesis ? { hypothesis } : {}),
        });
        setResult(res);
        return;
      }

      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      // Synthetic 8-event sample so the engine has something to chew on
      const events = [
        { id: 'e1', eventType: 'finding' as const, occurredAt: new Date(now.getTime() - 6 * 3600_000).toISOString(),
          piiCategories: ['aadhaar', 'minor_dob'], crossBorder: false, involvesMinor: true,
          originalDecision: 'allow', originalNotificationClockHours: 72 },
        { id: 'e2', eventType: 'decision' as const, occurredAt: new Date(now.getTime() - 5 * 3600_000).toISOString(),
          piiCategories: ['pan'], crossBorder: true, involvesMinor: false,
          originalDecision: 'mask', originalNotificationClockHours: 72 },
        { id: 'e3', eventType: 'breach_incident' as const, occurredAt: new Date(now.getTime() - 4 * 3600_000).toISOString(),
          piiCategories: ['aadhaar', 'pan'], crossBorder: false, involvesMinor: false,
          originalDecision: 'allow', originalNotificationClockHours: 72 },
        { id: 'e4', eventType: 'activity_event' as const, occurredAt: new Date(now.getTime() - 3 * 3600_000).toISOString(),
          piiCategories: ['aadhaar', 'minor_dob', 'health'], crossBorder: true, involvesMinor: true,
          originalDecision: 'allow', originalNotificationClockHours: 72 },
        { id: 'e5', eventType: 'finding' as const, occurredAt: new Date(now.getTime() - 2 * 3600_000).toISOString(),
          piiCategories: ['pan', 'gstin'], crossBorder: false, involvesMinor: false,
          originalDecision: 'mask', originalNotificationClockHours: 72 },
        { id: 'e6', eventType: 'decision' as const, occurredAt: new Date(now.getTime() - 1 * 3600_000).toISOString(),
          piiCategories: ['aadhaar'], crossBorder: false, involvesMinor: false,
          originalDecision: 'allow', originalNotificationClockHours: 72 },
        { id: 'e7', eventType: 'breach_incident' as const, occurredAt: new Date(now.getTime() - 0.5 * 3600_000).toISOString(),
          piiCategories: ['health', 'pii'], crossBorder: false, involvesMinor: false,
          originalDecision: 'mask', originalNotificationClockHours: 72 },
        { id: 'e8', eventType: 'finding' as const, occurredAt: now.toISOString(),
          piiCategories: ['aadhaar', 'minor_dob'], crossBorder: true, involvesMinor: true,
          originalDecision: 'allow', originalNotificationClockHours: 72 },
      ];
      const res = await runComplianceReplay({
        replayName: hypothesis || `Counter-factual ${new Date().toISOString().slice(0, 16)}`,
        fromTimestamp: yesterday.toISOString(),
        toTimestamp: now.toISOString(),
        targetRegulators: regs,
        events,
      });
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Replay failed');
    } finally {
      setRunning(false);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Compliance Replay Engine"
        subtitle='Counter-factual simulation across 13 regulators. "What changes if RBI 6h had applied 90 days earlier?" Answer in seconds, not weeks.'
        badges={[{ label: '13 regulators', variant: 'glass' }, { label: 'Counter-factual', variant: 'info' }]}
        actions={
          <Button data-action="disc.frontier_replay.a01 disc.frontier_replay.a02" size="sm" onClick={handleRun} disabled={running || selected.length === 0}>
            <Play className="h-4 w-4" /> {running ? 'Running…' : 'Run new replay'}
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <GlassCard className="p-6 lg:col-span-2">
          <CardHeader className="p-0 mb-3">
            <CardTitle>Build a counter-factual</CardTitle>
            <CardDescription clamp>Pick the regulators to apply, then replay your real events under that frame</CardDescription>
          </CardHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Event source</label>
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex rounded-md border border-border/40 overflow-hidden text-[11px]">
                  <button
                    onClick={() => setMode('historical')}
                    className={`px-3 py-1.5 transition-colors ${mode === 'historical' ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    Historical (real events)
                  </button>
                  <button
                    onClick={() => setMode('synthetic')}
                    className={`px-3 py-1.5 transition-colors border-l border-border/40 ${mode === 'synthetic' ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    Synthetic sample
                  </button>
                </div>
                {mode === 'historical' && (
                  <div className="inline-flex rounded-md border border-border/40 overflow-hidden text-[11px]">
                    {([7, 30, 90] as const).map((d) => (
                      <button
                        key={d}
                        onClick={() => setWindowDays(d)}
                        className={`px-2.5 py-1.5 transition-colors ${windowDays === d ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:text-foreground'} ${d !== 7 ? 'border-l border-border/40' : ''}`}
                      >
                        {d}d
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                {mode === 'historical'
                  ? `Pulls real activity events, DDR decisions, and breach incidents from the last ${windowDays} days.`
                  : 'Replays an 8-event illustrative sample - useful when no real history exists yet.'}
              </p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Hypothesis</label>
              <textarea
                value={hypothesis}
                onChange={(e) => setHypothesis(e.target.value)}
                placeholder='e.g. "If DPDPA Section 9 had been enforced 90 days earlier, what changes?"'
                rows={2}
                className="w-full glass-lite rounded-md p-3 text-sm outline-none resize-none"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Target regulators ({selected.length} selected)</label>
              <div className="flex flex-wrap gap-1.5">
                {REGULATORS.map((r) => {
                  const on = selected.includes(r.id);
                  return (
                    <button
                      key={r.id}
                      onClick={() => toggle(r.id)}
                      className={`text-[11px] px-2.5 py-1 rounded-md border transition-all ${
                        on ? 'border-primary bg-primary/15 text-foreground' : 'border-border bg-muted/30 text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <span>{r.name}</span>
                      <span className="ml-1.5 opacity-60 tabular">{r.clock}h</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <Button className="w-full" disabled={selected.length === 0 || running} onClick={handleRun}>
              <Play className="h-4 w-4" /> {running ? 'Running replay…' : 'Run replay'}
            </Button>
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <CardHeader className="p-0 mb-3">
            <CardTitle>Selected frame</CardTitle>
          </CardHeader>
          <div className="space-y-2">
            {selected.map((id) => {
              const r = REGULATORS.find((x) => x.id === id)!;
              return (
                <motion.div
                  key={id}
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="glass-lite p-2.5 rounded-md flex items-center justify-between"
                >
                  <span className="text-xs">{r.name}</span>
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[10px] tabular">{r.clock < 24 ? `${r.clock}h` : `${r.clock / 24}d`}</span>
                    {r.section9 && <ShieldOff className="h-3 w-3 text-warning" />}
                  </div>
                </motion.div>
              );
            })}
            {selected.length === 0 && <p className="text-xs text-muted-foreground italic text-center py-6">No regulators selected</p>}
          </div>
          <p className="text-[10px] text-muted-foreground mt-3 italic">
            <ShieldOff className="h-2.5 w-2.5 inline" /> = Section-9-equivalent (minor data hard-block)
          </p>
        </GlassCard>
      </div>

      {result && (
        <GlassCard className="p-6 mb-4 border-primary glow-low">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Live replay result - {result.replayName}</CardTitle>
            <CardDescription>Counter-factual evaluated against {result.perRegulator.length} regulator{result.perRegulator.length === 1 ? '' : 's'}</CardDescription>
          </CardHeader>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {result.perRegulator.map((r) => (
              <div key={r.regulator} className="glass-lite p-3 rounded-md">
                <div className="flex items-center justify-between mb-2">
                  <code className="text-xs font-mono">{r.regulator}</code>
                  <Badge variant="info" className="text-[9px]">{r.deltas.length} Δ</Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-[9px] uppercase text-muted-foreground">Tighter</div><div className="text-lg font-display tabular text-warning">{r.summary.clockTightenings}</div></div>
                  <div><div className="text-[9px] uppercase text-muted-foreground">Looser</div><div className="text-lg font-display tabular">{r.summary.clockLoosens}</div></div>
                  <div><div className="text-[9px] uppercase text-muted-foreground text-sev-critical">Block</div><div className="text-lg font-display tabular text-sev-critical">{r.summary.newHardBlocks}</div></div>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {error && (
        <GlassCard className="p-4 mb-4 border-destructive">
          <p className="text-xs text-destructive">Replay error: {error}</p>
        </GlassCard>
      )}

      <h2 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Recent replays</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {RECENT_REPLAYS.map((r, i) => (
          <motion.div key={r.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <GlassCard shine className="p-5 h-full">
              <div className="flex items-start justify-between mb-2">
                <div className="h-9 w-9 rounded-md bg-gradient-to-br from-chart-2/20 to-chart-7/20 flex items-center justify-center text-primary">
                  <History className="h-4 w-4" />
                </div>
                <Badge variant="glass" className="text-[9px]">{r.when}</Badge>
              </div>
              <CardTitle className="text-sm">{r.name}</CardTitle>
              <p className="text-xs text-muted-foreground mt-1 italic">"{r.hypothesis}"</p>
              <div className="flex flex-wrap gap-1 mt-3">
                {r.regulators.map((id) => <Badge key={id} variant="info" className="text-[9px]">{REGULATORS.find((x) => x.id === id)?.name}</Badge>)}
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-border/30">
                <div><div className="text-[10px] uppercase text-muted-foreground">Events replayed</div><div className="text-lg font-display tabular">{r.events.toLocaleString('en-IN')}</div></div>
                <div><div className="text-[10px] uppercase text-muted-foreground">Δ rows</div><div className="text-lg font-display tabular">{r.deltas}</div></div>
                <div><div className="text-[10px] uppercase text-muted-foreground text-sev-critical">New hard-blocks</div><div className="text-lg font-display tabular text-sev-critical">{r.newHardBlocks}</div></div>
                <div><div className="text-[10px] uppercase text-muted-foreground text-warning">Clock tightenings</div><div className="text-lg font-display tabular text-warning">{r.clockTightenings}</div></div>
              </div>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </PageContainer>
  );
}
